import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import Sidebar from '../components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import LanguageSelector from '../components/LanguageSelector';
import {
  Search,
  Plus,
  FileEdit,
  Download,
  Printer,
  Trash2,
  Edit,
  ScanLine,
  Calendar,
  Eye,
  Upload,
  Loader2,
  X,
  Mail,
  Send,
} from 'lucide-react';
import { exportTextDocumentToPDF } from '../utils/textDocumentPdf';
import { compressImage } from '../utils/imageCompress';
import { getTranslation } from '../i18n/translations';

export default function TextDocumentsPage({ user, logout, lang = 'tr', setLang }) {
  const t = (key) => getTranslation(lang, key);
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // OCR States
  const [isScanning, setIsScanning] = useState(false);
  const [scanPreview, setScanPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Email Modal State
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [emailForm, setEmailForm] = useState({
    recipient_email: '',
    recipient_name: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/text-documents');
      setDocuments(response.data);
    } catch (error) {
      toast.error(t('documentsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection for OCR
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('selectImageFile'));
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error(t('fileTooBig'));
      return;
    }

    // Görseli sıkıştır (büyük telefon fotoğraflarında network error'u önler)
    let compressed;
    try {
      compressed = await compressImage(file, { maxDim: 1600, quality: 0.85 });
      setScanPreview(compressed.dataUrl);
    } catch (err) {
      console.error('Compression error:', err);
      toast.error(t('imageProcessFailed') + ': ' + (err.message || t('unknownError')));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Start OCR scan
    setIsScanning(true);
    toast.info(t('scanningImage'));

    try {
      const formData = new FormData();
      formData.append('file', compressed.file);

      // NOTE: Do NOT set Content-Type manually for multipart/form-data.
      // The browser/axios must generate the boundary parameter automatically,
      // otherwise the request body is unparseable (causes silent failures on
      // mobile Safari / certain Cloudflare proxies).
      const response = await api.post('/ocr/scan', formData);

      if (response.data?.text) {
        // Navigate to OCR Editor with scanned text
        toast.success(t('scanSuccessRedirect'));
        
        // Store scanned text in sessionStorage for OCR Editor to pick up
        sessionStorage.setItem('ocr_scanned_text', response.data.text);
        sessionStorage.setItem('ocr_scan_time', Date.now().toString());
        
        setTimeout(() => {
          navigate('/ocr-editor');
        }, 500);
      } else {
        toast.error(t('scanEmptyResult'));
        setScanPreview(null);
      }
    } catch (error) {
      console.error('OCR Error:', error);
      toast.error(t('scanFailed') + ': ' + (error.response?.data?.detail || error.message || t('unknownError')));
      setScanPreview(null);
    } finally {
      setIsScanning(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleScanClick = () => {
    fileInputRef.current?.click();
  };

  const cancelScan = () => {
    setScanPreview(null);
    setIsScanning(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.content?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDeleteDocument'))) return;
    try {
      await api.delete(`/text-documents/${id}`);
      toast.success('Belge silindi');
      fetchDocuments();
    } catch (error) {
      toast.error(t('deleteFailed'));
    }
  };

  const handleDownloadPDF = async (doc) => {
    try {
      toast.info(t('pdfGenerating'));
      await exportTextDocumentToPDF(doc);
      toast.success('PDF indirildi');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('PDF indirilemedi');
    }
  };

  const handlePrint = async (doc) => {
    try {
      toast.info(t('printPreparing'));
      // Reuse exportTextDocumentToPDF with print:true so letterhead is consistent
      const { exportTextDocumentToPDF: exportPDF } = await import('../utils/textDocumentPdf');
      await exportPDF(doc, { print: true });
    } catch (error) {
      console.error('Print error:', error);
      toast.error(t('printError') + ': ' + (error.message || t('unknownError')));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateContent = (content, maxLength = 150) => {
    if (!content) return '';
    // Strip HTML tags
    const text = content.replace(/<[^>]*>/g, '');
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Email Functions
  const openEmailModal = (doc) => {
    setSelectedDocument(doc);
    setEmailForm({
      recipient_email: '',
      recipient_name: '',
      subject: `Belge: ${doc.title || 'Metin Belgesi'}`,
      message: ''
    });
    setEmailModalOpen(true);
  };

  const handleSendEmail = async () => {
    if (!emailForm.recipient_email) {
      toast.error(t('enterRecipientEmail'));
      return;
    }

    setSendingEmail(true);
    try {
      const response = await api.post(`/text-documents/${selectedDocument.id}/send-email`, {
        document_id: selectedDocument.id,
        recipient_email: emailForm.recipient_email,
        recipient_name: emailForm.recipient_name,
        subject: emailForm.subject,
        message: emailForm.message
      });
      
      toast.success(response.data.message);
      setEmailModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('emailSendFailed'));
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar user={user} lang={lang} />
      
      {/* Hidden file input for header scan button.
          NOTE: Using inline styles instead of `className="hidden"` (display:none)
          because some iOS Safari versions refuse to open the file picker on
          display:none inputs when triggered programmatically. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
        data-testid="ocr-file-input"
      />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{t('navTextDocs')}</h1>
                <p className="text-slate-500 text-xs sm:text-sm">{t('textDocsSubtitle')}</p>
              </div>
              <div className="flex items-center gap-2">
                <LanguageSelector currentLang={lang} onLanguageChange={setLang} />
                <Button 
                  onClick={handleScanClick}
                  disabled={isScanning}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm"
                  data-testid="scan-image-btn"
                >
                  {isScanning ? (
                    <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
                  ) : (
                    <ScanLine className="w-4 h-4 sm:mr-2" />
                  )}
                  <span className="hidden sm:inline">{isScanning ? t('scanning') : t('scanImage')}</span>
                </Button>
                <Button 
                  onClick={() => navigate('/ocr-editor')}
                  variant="outline"
                  size="sm"
                  className="border-amber-500 text-amber-600 hover:bg-amber-50 text-xs sm:text-sm"
                  data-testid="new-document-btn"
                >
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('newDocument')}</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 sm:p-6">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('totalDocuments')}</p>
                    <p className="text-2xl font-bold text-slate-900">{documents.length}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <FileEdit className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{t('thisMonth')}</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {documents.filter(d => {
                        const docDate = new Date(d.created_at);
                        const now = new Date();
                        return docDate.getMonth() === now.getMonth() && docDate.getFullYear() === now.getFullYear();
                      }).length}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-r from-purple-500 to-purple-600 border-0 relative overflow-hidden">
              <CardContent className="p-4">
                {/* Scan Preview Overlay */}
                {(scanPreview || isScanning) && (
                  <div className="absolute inset-0 bg-purple-900/95 flex flex-col items-center justify-center z-10 p-4">
                    {scanPreview && (
                      <img 
                        src={scanPreview} 
                        alt={t('scanning')} 
                        className="max-h-16 max-w-full rounded mb-2 opacity-50"
                      />
                    )}
                    {isScanning ? (
                      <div className="flex items-center gap-2 text-white">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm">{t('scanning')}</span>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={cancelScan}
                        className="text-white hover:bg-white/20"
                      >
                        <X className="w-4 h-4 mr-1" />
                        {t('cancel')}
                      </Button>
                    )}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="text-white">
                    <p className="text-sm opacity-90">{t('quickScan')}</p>
                    <p className="text-lg font-bold">{t('uploadImage')}</p>
                  </div>
                  <Button 
                    onClick={handleScanClick}
                    disabled={isScanning}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                    data-testid="quick-scan-btn"
                  >
                    {isScanning ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {isScanning ? t('scanning') : t('scanBtn')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <Card className="bg-white border-slate-200 mb-6">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={t('searchDocPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="document-search"
                />
              </div>
            </CardContent>
          </Card>

          {/* Documents Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredDocuments.length === 0 ? (
            <Card className="bg-white border-slate-200">
              <CardContent className="py-12 text-center">
                <FileEdit className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">
                  {searchTerm ? t('noSearchResults') : t('noDocumentsYet')}
                </p>
                <div className="flex justify-center gap-3">
                  <Button
                    onClick={handleScanClick}
                    disabled={isScanning}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {isScanning ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ScanLine className="w-4 h-4 mr-2" />
                    )}
                    {isScanning ? t('scanning') : t('scanImage')}
                  </Button>
                  <Button
                    onClick={() => navigate('/ocr-editor')}
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('createNewDocument')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDocuments.map((doc) => (
                <Card 
                  key={doc.id} 
                  className="bg-white border-slate-200 hover:shadow-lg transition-shadow"
                  data-testid={`document-card-${doc.id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <FileEdit className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <CardTitle className="text-base font-semibold text-slate-900 line-clamp-1">
                            {doc.title || t('untitledDocument')}
                          </CardTitle>
                          <p className="text-xs text-slate-500">
                            {formatDate(doc.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <p className="text-sm text-slate-600 line-clamp-3 mb-4 min-h-[60px]">
                      {truncateContent(doc.content)}
                    </p>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/ocr-editor/${doc.id}`)}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {t('edit')}
                      </Button>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEmailModal(doc)}
                          className="h-8 w-8 p-0"
                          title={t('sendEmailTitle')}
                          data-testid={`email-btn-${doc.id}`}
                        >
                          <Mail className="w-4 h-4 text-emerald-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadPDF(doc)}
                          className="h-8 w-8 p-0"
                          title={t('downloadPdf')}
                        >
                          <Download className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePrint(doc)}
                          className="h-8 w-8 p-0"
                          title={t('print')}
                        >
                          <Printer className="w-4 h-4 text-purple-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(doc.id)}
                          className="h-8 w-8 p-0"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" />
              {t('sendDocument')}
            </DialogTitle>
            <DialogDescription>
              {selectedDocument?.title || t('subjectDocumentPrefix')} — {t('sendByEmailDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient_email">{t('recipientEmail')} *</Label>
              <Input
                id="recipient_email"
                type="email"
                placeholder="ornek@email.com"
                value={emailForm.recipient_email}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_email: e.target.value })}
                data-testid="email-recipient-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recipient_name">{t('recipientName')}</Label>
              <Input
                id="recipient_name"
                placeholder={t('recipientName')}
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="subject">{t('subjectLabel')}</Label>
              <Input
                id="subject"
                placeholder={t('subjectLabel')}
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="message">{t('messageOptional')}</Label>
              <Textarea
                id="message"
                placeholder={t('messagePlaceholder')}
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                rows={4}
              />
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <p className="text-sm text-emerald-800">
                <strong>{t('senderLabel')}:</strong> info@nktteklif.com
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEmailModalOpen(false)}
              disabled={sendingEmail}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailForm.recipient_email}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="send-email-btn"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('sending')}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('sendBtn')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

